import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, Phone, Mail, MapPin, Instagram, Linkedin, Facebook, Twitter } from 'lucide-react';

const Footer = () => {
  const services = [
    { name: 'IT Consulting & Support', path: '/services#consulting' },
    { name: 'Remote Office Support', path: '/remote-office-support' },
    { name: 'Managed Services', path: '/services#managed' },
    { name: 'Backup & Disaster Recovery', path: '/backup-disaster-recovery-toronto' },
    { name: 'SaaS License Audit', path: '/saas-license-audit-toronto' },
    { name: 'Vendor Contract Management', path: '/vendor-contract-renegotiation-toronto' },
    { name: 'Telecom Cost Reduction', path: '/telecom-internet-cost-reduction-toronto' },
    { name: 'Hardware Solutions', path: '/services#hardware' },
  ];

  const industries = [
    { name: 'Law Firms', path: '/law-firm-it-support-toronto' },
    { name: 'Medical & Healthcare', path: '/medical-practice-it-support-toronto' },
    { name: 'Real Estate & Accounting', path: '/real-estate-accounting-it-support-toronto' },
    { name: 'Dental Offices', path: '/industries/dental-offices' },
    { name: 'Manufacturing', path: '/manufacturing-it-support' },
    { name: 'All Industries', path: '/industries' },
  ];

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' },
    { name: 'Knowledge Center', path: '/blog' },
    { name: 'IT Answers', path: '/answers' },
    { name: 'Case Studies', path: '/case-studies' },
  ];

  const serviceAreas = [
    { name: 'Toronto', path: '/services/it-support-toronto' },
    { name: 'Vaughan', path: '/it-services-vaughan' },
    { name: 'Mississauga', path: '/it-services-mississauga' },
    { name: 'Brampton', path: '/it-services-brampton' },
    { name: 'Markham', path: '/it-services-markham' },
    { name: 'Richmond Hill', path: '/it-services-richmond-hill' },
    { name: 'Newmarket', path: '/it-services-newmarket' },
    { name: 'Aurora', path: '/it-services-aurora' },
    { name: 'Oakville', path: '/it-services-oakville' },
    { name: 'Burlington', path: '/it-services-burlington' },
    { name: 'Etobicoke', path: '/it-services-etobicoke' },
  ];

  return (
    <footer className="bg-secondary text-secondary-foreground">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Company Info */}
          <div className="space-y-4 sm:space-y-6 sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 group">
              <Crown className="w-8 h-8 sm:w-10 sm:h-10 text-primary group-hover:scale-110 transition-transform" />
              <div className="flex flex-col">
                <span className="font-display font-bold text-lg sm:text-xl text-white leading-none">
                  KING<span className="text-primary">of</span>IT
                </span>
                <span className="text-[10px] sm:text-xs text-muted-foreground">IT Consulting</span>
              </div>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed">
              King of IT is a Toronto-based managed IT services company with 31 years of IT industry experience.
              Serving 500+ small and mid-sized businesses across the Greater Toronto Area.
            </p>
            <div className="flex items-center gap-3 sm:gap-4">
              <a 
                href="https://www.instagram.com/kingofit.ca/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a 
                href="#" 
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a 
                href="#" 
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <a 
                href="#" 
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all"
                aria-label="Twitter"
              >
                <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-semibold text-base sm:text-lg text-white mb-4 sm:mb-6">Our Services</h4>
            <ul className="space-y-2 sm:space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link 
                    to={service.path}
                    className="text-muted-foreground text-sm hover:text-primary transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="font-display font-semibold text-base sm:text-lg text-white mb-4 sm:mb-6">Industries</h4>
            <ul className="space-y-2 sm:space-y-3">
              {industries.map((industry) => (
                <li key={industry.name}>
                  <Link 
                    to={industry.path}
                    className="text-muted-foreground text-sm hover:text-primary transition-colors"
                  >
                    {industry.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display font-semibold text-base sm:text-lg text-white mb-4 sm:mb-6">Quick Links</h4>
            <ul className="space-y-2 sm:space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path}
                    className="text-muted-foreground text-sm hover:text-primary transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-display font-semibold text-base sm:text-lg text-white mb-4 sm:mb-6">Contact Us</h4>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <a 
                  href="tel:6479513653"
                  className="flex items-start gap-3 text-muted-foreground text-sm hover:text-primary transition-colors"
                >
                  <Phone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
                  <span>647-951-3653</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:support@kingofit.ca"
                  className="flex items-start gap-3 text-muted-foreground text-sm hover:text-primary transition-colors"
                >
                  <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
                  <span>support@kingofit.ca</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-muted-foreground text-sm">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
                  <span>1275 Finch Avenue West, Suite 608<br />Toronto, ON M3J 2B1</span>
                </div>
              </li>
            </ul>
            
            {/* Service Areas */}
            <div className="mt-4 sm:mt-6">
              <p className="text-xs text-muted-foreground mb-2">IT Support in:</p>
              <div className="flex flex-wrap gap-1 sm:gap-2">
                {serviceAreas.map((area) => (
                  <Link 
                    key={area.name} 
                    to={area.path}
                    className="text-xs text-muted-foreground bg-white/5 px-2 py-1 rounded hover:bg-primary hover:text-primary-foreground transition-colors"
                  >
                    {area.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-muted-foreground text-xs sm:text-sm text-center md:text-left">
              © {new Date().getFullYear()} King of IT Inc. All rights reserved. | 31 Years Experience | Toronto & GTA
            </p>
            <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-muted-foreground">
              <Link to="/contact" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link to="/contact" className="hover:text-primary transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
